import Vue from "vue";
import Router from "vue-router";
import Amplify, { Logger, I18n, Auth, AuthClass } from "aws-amplify";
import { components, AmplifyEventBus, AmplifyPlugin } from "aws-amplify-vue";
import store from "./store";
import Home from "./views/Home";
import Forms from "./views/Forms";
import FacebookForms from "./components/FacebookForms";
import GoogleForms from "./components/GoogleForms";
import SnapchatForms from "./components/SnapchatForms";
import CountryErrors from "./views/CountryErrors";
import BatchHistory from "./views/BatchHistory";
import AddForm from "./views/AddForm";
import EditForm from "./views/EditForm";
import EditGoogleForm from "./views/EditGoogleForm";
import Logs from "./views/Logs";
import LookupFields from "./views/LookupFields";
import UserManagement from "./views/UserManagement";
import ViewLog from "./views/ViewLog";
import ViewCountryError from "./views/ViewCountryError";

Vue.use(Router);
Vue.use(AmplifyPlugin, {
  AuthClass,
  Auth,
  Logger,
  I18n,
});

let user;

AmplifyEventBus.$on("authState", async (state) => {
  if (state === "signedOut") {
    user = null;
    store.commit("setUser", null);
    router.push({
      path: "/auth",
    });
  } else if (state === "signedIn") {
    user = await getUser();
    router.push({
      path: "/",
    });
  }
});

function getUser() {
  return Vue.prototype.$Amplify.Auth.currentAuthenticatedUser()
    .then((data) => {
      if (data && data.signInUserSession) {
        store.commit("setUser", data);

        return data;
      }
    })
    .catch((e) => {
      store.commit("setUser", null);
      return null;
    });
}

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/",
      name: "home",
      component: Home,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/forms",
      component: Forms,
      meta: {
        requiresAuth: true,
      },
      redirect: "/forms/facebook/",
      children: [
        {
          path: "facebook",
          component: FacebookForms,
        },
        {
          path: "google",
          component: GoogleForms,
        },
        {
          path: "snapchat",
          component: SnapchatForms,
        },
      ],
    },
    {
      path: "/country-errors",
      component: CountryErrors,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/show-batch-history",
      component: BatchHistory,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/add-form",
      component: AddForm,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/edit-form/:pageId/:formId",
      component: EditForm,
      meta: {
        requiresAuth: true,
      },
      props: true,
    },
    {
      path: "/edit-google-form/:webhookId",
      component: EditGoogleForm,
      meta: {
        requiresAuth: true,
      },
      props: true,
    },
    {
      path: "/view-logs/:formId",
      component: Logs,
      meta: {
        requiresAuth: true,
      },
      props: (route) => ({
        formId: route.params.formId,
      }),
    },
    {
      path: "/log-entry/:formId/:leadGenId",
      component: ViewLog,
      meta: {
        requiresAuth: true,
      },
      props: (route) => ({
        formId: route.params.formId,
        leadGenId: route.params.leadGenId,
        processor: route.query.processor,
      }),
    },
    {
      path: "/view-country-error/:formId/:leadGenId",
      component: ViewCountryError,
      meta: {
        requiresAuth: true,
      },
      props: (route) => ({
        formId: route.params.formId,
        leadGenId: route.params.leadGenId,
        processor: route.query.processor,
        retries: route.query.retries,
      }),
    },
    {
      path: "/view-batch-logs/:formId/:batchId",
      component: Logs,
      name: "batchRecords",
      meta: {
        requiresAuth: true,
      },
      props: (route) => ({
        batchId: route.params.batchId,
        formId: route.params.formId,
        pageName: decodeURIComponent(route.query.pageName),
        batchFormName: decodeURIComponent(route.query.formName),
      }),
    },
    {
      path: "/auth",
      name: "Authenticator",
      component: components.Authenticator,
    },
    {
      path: "/manage-lookups",
      component: LookupFields,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/manage-users",
      component: UserManagement,
      meta: {
        requiresAuth: true,
      },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return {
        x: 0,
        y: 0,
      };
    }
  },
});

router.beforeResolve(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    user = await getUser();
    if (!user) {
      return next({
        path: "/auth",
        query: {
          redirect: to.fullPath,
        },
      });
    }
    return next();
  }
  return next();
});

export default router;
